/** Figma 5702-12425 — Account settings layout tokens. */

export { homeContentInnerMaxWidth as ACCOUNT_PAGE_MAX_WIDTH } from '@/features/home/styles/homeStyles'
export const ACCOUNT_PROFILE_COLUMN_WIDTH = 627
export const ACCOUNT_BANNER_MAX_WIDTH = 734
export const ACCOUNT_PROFILE_BANNER_OVERLAP_PX = 95
export const ACCOUNT_PROFILE_AVATAR_OFFSET_PX = 104

/** Figma 5724-11865 — centered profile + grid column inside 884px card */
export const accountContentColumnSx = {
  width: '100%',
  maxWidth: ACCOUNT_PROFILE_COLUMN_WIDTH,
  mx: 'auto',
  boxSizing: 'border-box',
} as const

export const accountPageTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', md: '32px' },
  lineHeight: { xs: '32px', md: '40px' },
  letterSpacing: '0.4063px',
  color: '#0f1729',
  mt: { xs: 3, md: '25px' },
  mb: '8px',
} as const

export const accountPageSubtitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '21px',
  color: '#6b7a90',
  mb: { xs: 3, md: '24px' },
} as const

/** Figma 5702-12476 — folder-style tab bar */
export const accountTabBorder = '#e4e9f0'
export const accountTabInactive = '#a0abbf'
export const accountTabActive = '#008cd0'

export const accountTabBarSx = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '7px',
  borderBottom: `1px solid ${accountTabBorder}`,
  pt: '4px',
  minHeight: '48px',
  mb: 0,
  width: '100%',
  overflowX: { xs: 'auto', md: 'visible' },
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
} as const

export const accountTabButtonBaseSx = {
  appearance: 'none',
  WebkitAppearance: 'none',
  margin: 0,
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  textTransform: 'none' as const,
  minHeight: '42px',
  px: { xs: '16px', md: '31px' },
  py: '9px',
  borderRadius: 0,
  border: 'none',
  bgcolor: 'transparent',
  color: accountTabInactive,
  cursor: 'pointer',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.6)',
  },
} as const

export const accountTabButtonActiveSx = {
  ...accountTabButtonBaseSx,
  fontWeight: 600,
  color: accountTabActive,
  bgcolor: '#ffffff',
  border: `1.5px solid ${accountTabActive}`,
  borderBottom: 'none',
  borderRadius: '8px 8px 0 0',
  mb: '-1px',
  zIndex: 1,
  position: 'relative',
  minWidth: { xs: 'auto', sm: '178px' },
  '&:hover': {
    bgcolor: '#ffffff',
  },
} as const

/** Content panel attached below folder tabs — Figma 5702-12483 */
export const accountTabPanelSx = {
  bgcolor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderTop: 'none',
  borderRadius: '0 0 15px 15px',
  overflow: 'hidden',
} as const

export const accountTabSectionSx = {
  ...accountTabPanelSx,
  mb: 5,
} as const

export const accountCardSx = {
  bgcolor: '#ffffff',
  borderRadius: '15px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
} as const

/** Figma 5702-12485 — profile hero banner */
export const accountProfileBannerSx = {
  bgcolor: '#132640',
  height: 236,
  borderRadius: '30px',
  position: 'relative',
} as const

/** Figma 5724-11865 — white profile info panel under avatar */
export const accountProfilePanelSx = {
  position: 'relative',
  width: '100%',
  bgcolor: '#ffffff',
  borderRadius: '14.183px 14.183px 0 0',
  boxShadow: '0px 1.418px 4.255px rgba(13, 10, 44, 0.08)',
  /** 78px — 23px below avatar bottom (avatar ends at 55px when offset −104px) */
  pt: '78px',
  /** ~51px below contact row before demographic grid */
  pb: '51px',
  px: 0,
  boxSizing: 'border-box',
} as const

/** Figma 5702-12563 — pencil on white panel (15px from top, 10px from right of 627px column) */
export const accountProfileEditButtonSx = {
  position: 'absolute',
  top: '15px',
  right: '10px',
  zIndex: 3,
  color: '#008cd0',
  p: 0,
} as const

/** Figma 5702-12483 — white space below education row inside tab card (view mode) */
export const accountDetailsTabSx = {
  pb: '45px',
} as const

/** Figma 5729-11899 — edit mode avatar block (no banner / profile text) */
export const accountEditAvatarSectionSx = {
  display: 'flex',
  justifyContent: 'center',
  pt: '86px',
  pb: '62px',
} as const

/** Figma 5702-12642 — 63px camera control on avatar */
export const accountEditCameraButtonSx = {
  position: 'absolute',
  right: '-12px',
  bottom: '-12px',
  width: 63,
  height: 63,
  bgcolor: '#008cd0',
  color: '#ffffff',
  border: '4px solid #ffffff',
  zIndex: 2,
  '&:hover': { bgcolor: '#007ab8' },
} as const

export const accountAvatarUploadAvatarSx = {
  position: 'relative',
  display: 'inline-flex',
} as const

/** Fixed slot below avatar — always in layout; camera overlaps from above; buttons overlay when previewing */
export const accountAvatarUploadActionZoneSx = {
  position: 'relative',
  width: '100%',
  height: '40px',
  flexShrink: 0,
} as const

export const accountAvatarUploadActionsSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
} as const

/** Centered in action zone — does not change layout height */
export const accountAvatarUploadActionsOverlaySx = {
  ...accountAvatarUploadActionsSx,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  whiteSpace: 'nowrap',
  zIndex: 1,
} as const

export const accountAvatarUploadRootSx = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const

const accountAvatarUploadButtonBaseSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '13px',
  lineHeight: '19.5px',
  textTransform: 'none' as const,
  borderRadius: '8px',
  minWidth: 'auto',
  height: 36,
  px: '16px',
  py: 0,
  boxShadow: 'none',
} as const

export const accountAvatarUploadCancelButtonSx = {
  ...accountAvatarUploadButtonBaseSx,
  fontWeight: 400,
  bgcolor: '#ffffff',
  color: '#64748b',
  border: '1px solid #e2e8f0',
  '&:hover': { bgcolor: '#f8fafc', boxShadow: 'none' },
} as const

export const accountAvatarUploadConfirmButtonSx = {
  ...accountAvatarUploadButtonBaseSx,
  bgcolor: '#008cd0',
  color: '#ffffff',
  border: '1px solid #008cd0',
  '&:hover': { bgcolor: '#007ab8', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#ffffff', borderColor: '#94a3b8' },
} as const

/** Figma 5729-11899 — edit form grid inside 884px tab panel */
export const accountFormGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: '10px',
  rowGap: '36px',
  alignItems: 'start',
  px: { xs: '16px', md: '26px' },
} as const

export const accountFormLabelSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '13px',
  lineHeight: '19.5px',
  color: '#6b7a90',
  display: 'block',
} as const

export const accountFormRequiredMarkSx = {
  color: '#ff5959',
} as const

const accountWebkitAutofillSx = (bg: string, textColor: string) =>
  ({
    WebkitBoxShadow: `0 0 0 100px ${bg} inset`,
    WebkitTextFillColor: textColor,
    caretColor: textColor,
    transition: 'background-color 5000s ease-in-out 0s',
  }) as const

const accountWebkitAutofillEnabled =
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active'

const accountWebkitAutofillDisabled =
  '&.Mui-disabled input:-webkit-autofill, &.Mui-disabled input:-webkit-autofill:hover, &.Mui-disabled input:-webkit-autofill:focus, &.Mui-disabled input:-webkit-autofill:active'

export const accountFormInputRootSx = {
  height: 48,
  borderRadius: '10px',
  bgcolor: '#ffffff',
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: '16px', md: '15px' },
  lineHeight: '22.5px',
  color: '#0d1b2a',
  '& fieldset': { borderWidth: 1, borderColor: '#e0e6ea' },
  '&:hover fieldset': { borderColor: '#e0e6ea' },
  '&.Mui-focused fieldset': { borderWidth: 1, borderColor: '#008cd0' },
  '&.Mui-disabled': { bgcolor: '#e2e8f0' },
  [accountWebkitAutofillEnabled]: accountWebkitAutofillSx('#ffffff', '#0d1b2a'),
  [accountWebkitAutofillDisabled]: accountWebkitAutofillSx('#e2e8f0', '#0d1b2a'),
  '& .MuiOutlinedInput-input': {
    px: '14px',
    py: 0,
    height: 48,
    boxSizing: 'border-box',
  },
} as const

export const accountFormInputSx = {
  '& .MuiOutlinedInput-root': accountFormInputRootSx,
  '& .MuiFormHelperText-root': { display: 'none' },
} as const

/** Figma 5702-12821 — locked email read-only box (solid #e2e8f0, not MUI disabled input) */
export const accountFormEmailLockedSx = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: 48,
  bgcolor: '#e2e8f0',
  border: '1px solid #e0e6ea',
  borderRadius: '10px',
  pl: '22px',
  pr: '40px',
  boxSizing: 'border-box',
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  color: '#0d1b2a',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'not-allowed',
} as const

export const accountFormSelectSx = {
  height: 48,
  borderRadius: '10px',
  fontFamily: '"Inter", sans-serif',
  fontSize: '15px',
  color: '#0d1b2a',
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: '#e0e6ea' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e6ea' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: '#008cd0' },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: '12px',
    pl: '14px',
    pr: '40px',
  },
} as const

/**
 * Figma FormField — label + 6px gap + 48px control (73.5px). Helpers sit in the 36px
 * row gap via absolute positioning so validation never shifts the grid.
 */
export const accountFormFieldCellSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  position: 'relative',
  minHeight: '73.5px',
} as const

/** 5px below the 48px control — Figma email / “Not Provided” offset */
export const accountFormHelperBaseSx = {
  position: 'absolute',
  left: 0,
  top: '78.5px',
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '10px',
  lineHeight: 'normal',
  display: 'block',
  pointerEvents: 'none',
} as const

export const accountFormHelperInfoSx = {
  ...accountFormHelperBaseSx,
  color: '#3b82f6',
  pointerEvents: 'auto',
} as const

export const accountFormHelperErrorSx = {
  ...accountFormHelperBaseSx,
  fontSize: '12px',
  color: '#d32f2f',
  pointerEvents: 'auto',
} as const

/** Figma 5702-12829 / 5702-12833 — edit mode actions (align with right form column) */
export const accountEditActionsSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: '10px',
  mt: { xs: '48px', md: '140px' },
  mb: '36px',
  pl: { xs: '16px', md: '26px' },
  pr: { xs: '16px', md: '32px' },
} as const

export const accountEditActionsInnerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: { xs: 'stretch', sm: 'flex-end' },
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  gap: { xs: '12px', md: '48px' },
  gridColumn: { xs: '1 / -1', sm: '2 / 3' },
} as const

export const accountEditSaveButtonSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '21px',
  textTransform: 'none' as const,
  borderRadius: '10px',
  bgcolor: '#008cd0',
  color: '#ffffff',
  width: { xs: '100%', sm: 160 },
  minWidth: { xs: 0, sm: 160 },
  height: 48,
  boxShadow: '0px 2px 4px #dce9ff',
  border: '1px solid #008cd0',
  '&:hover': { bgcolor: '#007ab8', boxShadow: '0px 2px 4px #dce9ff' },
  '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#ffffff', borderColor: '#94a3b8' },
} as const

export const accountEditCancelButtonSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  textTransform: 'none' as const,
  color: '#64748b',
  minWidth: 'auto',
  px: 0,
  py: 0,
  '&:hover': { bgcolor: 'transparent', color: '#475569' },
} as const

export const accountProfileContactRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: 452,
  width: '100%',
  mx: 'auto',
  mt: '40px',
  gap: 2,
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  px: { xs: 2, md: 0 },
} as const

export const accountProfileEmailSpacingSx = {
  mt: '1px',
} as const

export const accountDemographicLabelValueGapSx = {
  mt: '8px',
} as const

/** Figma 5702-12549 / 5702-12633 — avatar frame */
export const accountAvatarFrameSx = {
  width: 159,
  height: 159,
  borderRadius: '32px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const

/** View mode — placeholder avatar, no gray border (Figma 5702-12549) */
export const accountAvatarEmptyViewSx = {
  ...accountAvatarFrameSx,
  bgcolor: '#ebf0f6',
} as const

/** Edit mode — placeholder avatar with gray border (Figma 5702-12633) */
export const accountAvatarEmptyEditSx = {
  ...accountAvatarFrameSx,
  bgcolor: '#ebf0f6',
  border: '1px solid #94a3b8',
} as const

export const accountAvatarImageViewSx = {
  ...accountAvatarFrameSx,
  border: '4px solid rgba(255, 255, 255, 0.1)',
} as const

/** Edit mode — uploaded avatar with gray border */
export const accountAvatarImageEditSx = {
  ...accountAvatarFrameSx,
  border: '1px solid #94a3b8',
} as const

export const accountProfileNameSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: { xs: '24px', md: '35px' },
  lineHeight: { xs: '32px', md: '56px' },
  color: '#000000',
  textAlign: 'center',
  px: { xs: 2, md: 0 },
  overflowWrap: 'anywhere',
} as const

export const accountProfileEmailSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: { xs: '16px', md: '20px' },
  lineHeight: { xs: '22px', md: '22.5px' },
  color: '#008cd0',
  textAlign: 'center',
  overflowWrap: 'anywhere',
} as const

export const accountProfileLocationSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '22.5px',
  color: '#0d9467',
  /** Figma 5702-12557 — 1px below icon cap line */
  mt: '1px',
} as const

export const accountProfilePhoneSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  color: '#df6534',
} as const

export const accountDemographicGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  width: '100%',
  boxSizing: 'border-box',
  mt: 0,
  '& > *:nth-child(2n+1)': {
    marginLeft: 0,
  },
  '& > *:nth-child(-n+2)': {
    marginTop: 0,
  },
  /** Figma 5702-12566 — last row: education (left) + housing (right) */
  '& > *:nth-last-child(2)': {
    borderRadius: { xs: 0, sm: '0 0 0 15px' },
    overflow: 'hidden',
  },
  '& > *:last-child': {
    borderRadius: { xs: '0 0 15px 15px', sm: '0 0 15px 0' },
    overflow: 'hidden',
  },
} as const

const accountDemographicCellBaseSx = {
  display: 'flex',
  gap: '28px',
  height: 87,
  minHeight: 87,
  pl: '17px',
  pr: '16px',
  bgcolor: '#ffffff',
  boxSizing: 'border-box',
  borderTop: '1px solid #e2e8f0',
  borderLeft: '1px solid #e2e8f0',
  borderRight: '1px solid #e2e8f0',
  borderBottom: '1px solid #e2e8f0',
  marginTop: '-1px',
  marginLeft: '-1px',
} as const

/** Figma 5702-12488 / 5702-12515 — gender + marital row (vertically centered) */
export const accountDemographicCellFirstRowSx = {
  ...accountDemographicCellBaseSx,
  alignItems: 'center',
  py: '18px',
} as const

/** Figma 5702-12495+ — icon at 32px, label block at 20px from cell top */
export const accountDemographicCellSx = {
  ...accountDemographicCellBaseSx,
  alignItems: 'flex-start',
  pt: '20px',
  pb: '18px',
} as const

export const accountDemographicIconOffsetSx = {
  mt: '12px',
} as const

/** Figma 5702-12566 — full-width last row with rounded bottom corners */
export const accountDemographicEducationCellSx = {
  ...accountDemographicCellSx,
  width: '100%',
  marginLeft: 0,
  borderLeft: '1px solid #e2e8f0',
  borderRight: '1px solid #e2e8f0',
  borderRadius: '0 0 15px 15px',
  overflow: 'hidden',
} as const

/** Fixed 24×24 slot — icons keep aspect ratio (no stretch). */
export const accountDemographicIconWrapSx = {
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0f172a',
  '& svg': {
    width: 24,
    height: 24,
    display: 'block',
  },
} as const

export const accountDemographicValueMutedSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '19.5px',
  color: '#6b7a90',
  display: 'block',
} as const

export const accountFieldLabelSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '19.5px',
  color: '#0f172a',
  display: 'block',
} as const

/** Figma — gender + marital values */
export const accountDemographicValuePrimarySx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  color: '#64748b',
  display: 'block',
} as const

export const accountFieldValueSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '22.5px',
  color: '#0d1b2a',
  display: 'block',
} as const

export const accountNotProvidedSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '19.5px',
  color: '#6b7a90',
  display: 'block',
} as const

export const accountPrimaryButtonSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  textTransform: 'none' as const,
  borderRadius: '10px',
  bgcolor: '#008cd0',
  color: '#ffffff',
  px: 3,
  py: '12px',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#007ab8', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#ffffff' },
} as const

export const accountSecondaryButtonSx = {
  ...accountPrimaryButtonSx,
  bgcolor: '#ffffff',
  color: '#0d1b2a',
  border: '2px solid #e5e7eb',
  '&:hover': { bgcolor: '#f8fafc', boxShadow: 'none' },
} as const

export const accountOutlinedInputSx = {
  '& .MuiOutlinedInput-root': {
    height: 52,
    borderRadius: '10px',
    bgcolor: '#ffffff',
    fontFamily: '"Inter", sans-serif',
    fontSize: '16px',
    '& fieldset': { borderWidth: 2, borderColor: '#e5e7eb' },
    '&:hover fieldset': { borderColor: '#e5e7eb' },
    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: '#008cd0' },
    '&.Mui-disabled': { bgcolor: '#f1f5f9' },
  },
} as const

/** Figma 5702-12886 / 5702-13233 — Change Password tab panel */
export const ACCOUNT_PASSWORD_FIELD_WIDTH = 403

/** Figma 5702-13276 — success hint after current password verified (single line) */
export const accountPasswordVerifiedMessageSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#0d9467',
  mt: '13px',
  whiteSpace: { xs: 'normal', md: 'nowrap' },
} as const

/** Figma 5702-13239 — verified current password locked (matches account locked email #e2e8f0) */
export const ACCOUNT_VERIFIED_FIELD_LOCKED_BG = '#e2e8f0'

export const accountPasswordVerifiedInputSx = {
  '&.Mui-disabled': {
    bgcolor: ACCOUNT_VERIFIED_FIELD_LOCKED_BG,
    color: '#0f172a',
    WebkitTextFillColor: '#0f172a',
  },
  '&.Mui-disabled fieldset': {
    borderColor: '#e0e6ea',
  },
  '&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiInputBase-input': {
    background: ACCOUNT_VERIFIED_FIELD_LOCKED_BG,
    backgroundColor: ACCOUNT_VERIFIED_FIELD_LOCKED_BG,
  },
  '&.Mui-disabled .MuiInputAdornment-root': {
    background: ACCOUNT_VERIFIED_FIELD_LOCKED_BG,
    backgroundColor: ACCOUNT_VERIFIED_FIELD_LOCKED_BG,
  },
  [accountWebkitAutofillDisabled]: accountWebkitAutofillSx(
    ACCOUNT_VERIFIED_FIELD_LOCKED_BG,
    '#0f172a',
  ),
} as const

/** Figma 5702-13250–13264 — 11px gap between confirm field and requirements box */
export const accountPasswordConfirmChecklistGroupSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '11px',
  width: '100%',
  maxWidth: ACCOUNT_PASSWORD_FIELD_WIDTH,
} as const

export const accountPasswordTabSx = {
  px: { xs: '16px', md: '32px' },
  pt: { xs: '24px', md: '32px' },
  pb: { xs: '24px', md: '32px' },
  display: 'flex',
  flexDirection: 'column',
} as const

export const accountPasswordHeaderSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  mb: '45px',
} as const

export const accountPasswordTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '28px',
  letterSpacing: '-0.4492px',
  color: '#0f172a',
} as const

export const accountPasswordSubtitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#64748b',
} as const

export const accountPasswordFieldLabelSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  display: 'block',
} as const

export const accountPasswordFieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  maxWidth: ACCOUNT_PASSWORD_FIELD_WIDTH,
} as const

export const accountPasswordInputSx = {
  width: '100%',
  maxWidth: ACCOUNT_PASSWORD_FIELD_WIDTH,
  height: 46,
  borderRadius: '10px',
  bgcolor: '#ffffff',
  fontFamily: '"Inter", sans-serif',
  fontSize: '14px',
  lineHeight: '20px',
  color: '#0f172a',
  '& fieldset': { borderWidth: 1, borderColor: '#e5e7eb' },
  '&:hover fieldset': { borderColor: '#e5e7eb' },
  '&.Mui-focused fieldset': { borderWidth: 1, borderColor: '#008cd0' },
  '&.Mui-disabled': { bgcolor: '#f8fafc' },
  [accountWebkitAutofillEnabled]: accountWebkitAutofillSx('#ffffff', '#0f172a'),
  [accountWebkitAutofillDisabled]: accountWebkitAutofillSx('#f8fafc', '#0f172a'),
  '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
    px: '15px',
    py: 0,
    height: 46,
    boxSizing: 'border-box',
    background: '#ffffff',
    backgroundColor: '#ffffff',
    '&::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    mr: '15px',
    background: '#ffffff',
    backgroundColor: '#ffffff',
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    bgcolor: 'transparent',
    backgroundColor: 'transparent',
    '&:hover': {
      bgcolor: 'transparent',
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      bgcolor: 'transparent',
      backgroundColor: 'transparent',
    },
  },
  '&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiInputBase-input': {
    background: '#f8fafc',
    backgroundColor: '#f8fafc',
  },
  '&.Mui-disabled .MuiInputAdornment-root': {
    background: '#f8fafc',
    backgroundColor: '#f8fafc',
  },
} as const

export const accountPasswordFieldsStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '27px',
} as const

/** Figma 5702-12920 — fixed width so loading spinner does not shrink the button */
export const ACCOUNT_PASSWORD_PRIMARY_BUTTON_WIDTH = 186

export const accountPasswordPrimaryButtonSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: 0,
  textTransform: 'none' as const,
  borderRadius: '12px',
  minWidth: { xs: 0, sm: ACCOUNT_PASSWORD_PRIMARY_BUTTON_WIDTH },
  width: { xs: '100%', sm: ACCOUNT_PASSWORD_PRIMARY_BUTTON_WIDTH },
  maxWidth: ACCOUNT_PASSWORD_PRIMARY_BUTTON_WIDTH,
  minHeight: 40,
  height: 40,
  px: '16px',
  py: '10px',
  boxSizing: 'border-box',
  bgcolor: '#008cd0',
  color: '#f8fafc',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#007ab8', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#f8fafc' },
} as const
